import { useCallback, useMemo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslator } from '@picsart/rc/services/localization';
import Slider from '@picsart/slider/Slider';
import useMount from '@picsart/rc/useMount';
import { FontWeights, Spacings, Typography } from '@picsart/cascade';
import { Text, TypographyHorizontalAlign } from '@picsart/text';
import View from '@picsart/view/View';
import { Progress, ProgressStatuses } from '@picsart/progress';
import Dynamic from '@picsart/editorlayout/Dynamic';
import DynamicHeader from '@picsart/editorlayout/DynamicHeader';
import DynamicBody from '@picsart/editorlayout/DynamicBody';

import useBatchData from 'pages/Batch/sections/Context/useBatchData';
import { BATCH_ACTIONS, BATCH_TOOLS } from 'pages/Batch/configs';
import analytics, { SubToolNames } from 'pages/Batch/utils/analytics';
import { getEnhanceIntensity } from 'store/batch/selectors';
import { BatchDataStatus } from 'configs/constants';
import batchSlice from 'store/batch';
import useHeaderStyles from 'pages/Batch/styles';

import { IProcessingTool } from '../types';

import useStyles from './styles';

// needed for properly showing the progress
const DELAY_FOR_SUCCESS = 1500;

const AIEnhanceTool = ({ inProgress }: IProcessingTool) => {
  const styles = useStyles();
  const headerClasses = useHeaderStyles();
  const dispatch = useDispatch();
  const translate = useTranslator();
  const { process, selectedData } = useBatchData();
  const [delayedProgress, setDelayedProgress] = useState(inProgress);

  const intensity = useSelector(getEnhanceIntensity);
  const doneItemsCount = useMemo(() => selectedData.filter(({ status }) => status === BatchDataStatus.DONE).length, [selectedData]);

  useMount(() => {
    analytics.openEvent(BATCH_TOOLS.enhance);
    process(BATCH_ACTIONS.aiEnhance, {});
    analytics.utils().setSubTool(BATCH_TOOLS.enhance, {
      name: SubToolNames.intensity,
      value: '100%',
    });
    return () => {
      dispatch(batchSlice.actions.resetEnhanceMapping());
      dispatch(batchSlice.actions.resetIntensity());
    };
  });

  const handleIntensityChange = useCallback((value: number) => {
    analytics.utils().setSubTool(BATCH_TOOLS.enhance, {
      name: SubToolNames.intensity,
      value: `${value * 10}%`,
    });
    dispatch(batchSlice.actions.setEnhanceIntensity(value / 10));
  }, [dispatch]);

  useEffect(() => {
    if (inProgress) {
      setDelayedProgress(true);
      return;
    }
    const timeoutId = setTimeout(() => {
      setDelayedProgress(inProgress);
    }, DELAY_FOR_SUCCESS);

    return () => clearTimeout(timeoutId);
  }, [inProgress]);

  return (
    <Dynamic
      header={(
        <DynamicHeader
          className={headerClasses.customHeader}
          title={translate('AI Enhance')}
        />
      )}
      body={(
        <DynamicBody>
          {delayedProgress ? (
            <View marginBlock={Spacings.s6}>
              <View marginBlockEnd={Spacings.s12}>
                <Text
                  align={TypographyHorizontalAlign.Left}
                  size={Typography.t4}
                >
                  {translate('Our AI is enhancing your photo. This might take a while.')}
                </Text>
              </View>
              <View flexDirection="row" width="100%">
                <Progress
                  percent={100 * doneItemsCount / selectedData.length}
                  status={doneItemsCount === selectedData.length ? ProgressStatuses.Success : ProgressStatuses.Default}
                />
                <Text className={styles.progressText} weight={FontWeights.SemiBold} size={Typography.t4}>{`${doneItemsCount}/${selectedData.length}`}</Text>
              </View>
            </View>
          ) : (
            <View marginBlock={Spacings.s8} flexDirection="column">
              <Text weight={FontWeights.SemiBold}>
                {translate('Intensity')}
              </Text>
              <View alignItems="center">
                <Slider
                  max={10}
                  min={0}
                  step={1}
                  value={intensity * 10}
                  className={styles.slider}
                  onChange={value => handleIntensityChange(value as number)}
                />
                <Text weight={FontWeights.Medium}>{`${intensity * 100}%`}</Text>
              </View>
            </View>
          )}
        </DynamicBody>
      )}
    />
  );
};

export default AIEnhanceTool;
